import { useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api, errorMessage } from '../lib/api';
import { sessionKey, useSession } from '../lib/session';
import { AuthLayout } from '../components/AuthLayout';
import { Alert, LoadingBlock } from '../components/ui';

export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const qc = useQueryClient();
  const { data: session } = useSession();
  const signedIn = session?.status === 'signed_in';
  const started = useRef(false);

  const verify = useMutation({
    mutationFn: () => api.post<{ email: string }>('/auth/verify-email', { token }),
    onSuccess: () => qc.invalidateQueries({ queryKey: sessionKey }),
  });
  useEffect(() => {
    if (!started.current && token) {
      started.current = true;
      verify.mutate();
    }
  }, [token, verify]);

  if (!token) {
    return (
      <AuthLayout title="This link is incomplete">
        <Alert>Copy the whole link from the email into your browser, or open the email and click the button.</Alert>
      </AuthLayout>
    );
  }
  if (verify.isError) {
    return (
      <AuthLayout title="We couldn’t confirm your email">
        <Alert>{errorMessage(verify.error)}</Alert>
        <Link to={signedIn ? '/account' : '/login'} className="btn-secondary mt-5 w-full">
          {signedIn ? 'Go to your account' : 'Sign in'}
        </Link>
      </AuthLayout>
    );
  }
  if (verify.isSuccess) {
    return (
      <AuthLayout title="Email confirmed" description="You’re all set. You can now create your first team.">
        <Link to={signedIn ? '/' : `/login?email=${encodeURIComponent(verify.data.email)}`} className="btn-primary btn-lg w-full">
          {signedIn ? 'Go to my teams' : 'Sign in'}
        </Link>
      </AuthLayout>
    );
  }
  return (
    <AuthLayout title="Confirming your email…">
      <LoadingBlock />
    </AuthLayout>
  );
}
